import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePlanDetail } from '../hooks/usePlans';
import { useUserStore } from '../stores';
import { ArrowLeft, ChevronRight, MapPin } from 'lucide-react';
import UnifiedMap from '../components/UnifiedMap';
import { planDaysToMarkers, planActivitiesToMarkers } from '../utils/mapMarkers';
import ForkModal from '../components/plans/ForkModal';
import { storeForkIntent } from '../components/plans/ForkGateModal';
import ShareSheet from '../components/plans/ShareSheet';
import { ReportContentModal } from '../components/common/ReportContentModal';
import CreatorMiniPanel from '../components/plans/CreatorMiniPanel';
import { BottomNav } from '../components/navigation';
import { CommunityActivityThumb } from '../components/plans/CommunityActivityThumb';
import { trackPlanViewed, trackPlanForked, trackEvent } from '../hooks/useAnalytics';
import { voteActivity, getVoteSummary, VoteSummary } from '../services/plansApi';
import { resolveInvitation } from '../services/plannerApi';
import SuggestModal from '../components/plans/SuggestModal';
import SharedItinerary from './SharedItinerary';
import './PlansV2.css';
import './PlanDetailV2.css';

const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`;

const PlanDetailV2: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  // Every render decision below reads only what the hook answers for THIS
  // navigation's request — never the store's retained detail or its error.
  const { plan, isLoading, notFound, failed, forkPlan } = usePlanDetail(slug);

  const [mapDay, setMapDay] = useState<number | null>(null);
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(new Set());
  const [showForkModal, setShowForkModal] = useState(false);
  const [showShareSheet, setShowShareSheet] = useState<number | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [creatorPopover, setCreatorPopover] = useState<{ name: string; rect: DOMRect | null } | null>(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [votes, setVotes] = useState<Record<string, 'interested' | 'not_for_me'>>({});
  const [voteSummary, setVoteSummary] = useState<VoteSummary | null>(null);
  const [sessionId] = useState(() => localStorage.getItem('vizday_vote_session') || (() => {
    const id = Math.random().toString(36).slice(2);
    localStorage.setItem('vizday_vote_session', id);
    return id;
  })());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Fetch vote summary
  useEffect(() => {
    if (plan) {
      getVoteSummary(plan.id).then(setVoteSummary).catch(() => {});
    }
  }, [plan]);

  const [sharedBy, setSharedBy] = useState<string | null>(null);
  const [inviteContext, setInviteContext] = useState<{
    creator_name: string;
    destination: string;
  } | null>(null);

  // Phase C-3: Invitation detection (verify plan matches by slug)
  useEffect(() => {
    if (!plan) return;
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite');
    if (inviteToken) {
      resolveInvitation(inviteToken)
        .then((data) => {
          // Only show banner if invitation's plan slug matches current page
          if (data.plan_slug === slug) {
            setInviteContext({ creator_name: data.creator_name, destination: data.destination });
          }
        })
        .catch(() => {});
    }
  }, [plan, slug]);

  useEffect(() => {
    if (plan) {
      const params = new URLSearchParams(window.location.search);
      const from = params.get('from') || null;
      const inviteToken = params.get('invite') || null;
      setSharedBy(from);
      trackPlanViewed(plan.id, 'detail');
      trackEvent('plan_page_visit', {
        plan_id: plan.id,
        slug: plan.slug,
        referrer: document.referrer || '',
        from: from || undefined,
        ...(inviteToken ? { source: 'invitation' } : {}),
      });
    }
  }, [plan]);

  // Resume a fork interrupted by signup: useUserStore redirects back here
  // with ?fork=1 after auth, so reopen the modal instead of making the user
  // find the button again.
  useEffect(() => {
    if (!plan || !user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('fork') === '1') {
      params.delete('fork');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      setShowForkModal(true);
    }
  }, [plan, user]);

  const handleFork = () => {
    if (!plan) return;
    if (!user) {
      if (slug) storeForkIntent(slug);
      navigate('/login', { state: { returnTo: window.location.pathname } });
      return;
    }
    setShowForkModal(true);
  };

  const handleForkConfirm = async (startDate: string): Promise<{ id: number }> => {
    if (!plan) throw new Error('No plan');
    const result = await forkPlan(plan.id, startDate);
    trackPlanForked(plan.id, plan.destination);
    return { id: (result as { id: number }).id };
  };

  const handleVote = async (actTitle: string, dayNum: number, sentiment: 'interested' | 'not_for_me') => {
    if (!plan) return;
    const newSentiment = votes[actTitle] === sentiment ? undefined : sentiment;
    setVotes(prev => {
      const next = { ...prev };
      if (newSentiment) next[actTitle] = newSentiment;
      else delete next[actTitle];
      return next;
    });
    if (newSentiment) {
      voteActivity(plan.id, actTitle, dayNum, newSentiment, sessionId).catch(() => {});
    }
  };

  const toggleDay = (dayNum: number) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayNum)) next.delete(dayNum);
      else next.add(dayNum);
      return next;
    });
  };

  const toggleAllDays = () => {
    if (!plan) return;
    if (collapsedDays.size === 0) {
      setCollapsedDays(new Set(plan.days.map(d => d.day)));
    } else {
      setCollapsedDays(new Set());
    }
  };

  const mapMarkers = useMemo(() => {
    if (!plan) return [];
    if (mapDay) {
      const day = plan.days.find(d => d.day === mapDay);
      return day ? planActivitiesToMarkers(day.activities, mapDay) : [];
    }
    return planDaysToMarkers(plan.days);
  }, [plan, mapDay]);

  // No published CommunityPlan holds this slug: the page falls back to the
  // public itinerary that does, if any. Only on a handled 404 for this
  // navigation — a 5xx stays "not found" rather than showing an itinerary
  // in place of a community plan that exists.
  if (notFound && slug) {
    return <SharedItinerary source={{ kind: 'slug', slug }} />;
  }

  // Loading
  if (isLoading) {
    return (
      <div className="pv2-page">
        <div className="pv2-loading">Loading plan...</div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  // Not found
  if (failed || !plan) {
    return (
      <div className="pv2-page">
        <div className="pv2-not-found">
          <p className="pv2-not-found-title">Plan not found</p>
          <p className="pv2-not-found-body">This plan may have been removed or the link is incorrect.</p>
          <Link to="/explore" className="pv2-back-link">Back to Explore</Link>
        </div>
        <BottomNav activeItem="explore" />
      </div>
    );
  }

  const totalActivities = plan.days.reduce((sum, d) => sum + d.activities.length, 0);

  return (
    <div className="pv2-page pdv2">
      {/* Hero — paper with a pin glyph (plan decisions 4, 20) */}
      <div className="pdv2-hero">
        <div className="pdv2-hero-gradient">
          <MapPin size={48} aria-hidden="true" />
        </div>
        <div className="pdv2-hero-controls">
          <button className="pdv2-back-btn" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}>
            <ArrowLeft size={14} />
          </button>
        </div>
        <div className="pdv2-hero-dest-label">
          <span>{plan.destination}</span>
        </div>
        <div className="pdv2-hero-url-label">
          <span>vizday.com/plans/{slug}</span>
        </div>
      </div>

      {/* Invitation banner (Phase C-3) — overrides social provenance */}
      {inviteContext ? (
        <div className="pdv2-invite-banner">
          <strong>{inviteContext.creator_name}</strong> invited you to plan <strong>{inviteContext.destination}</strong> together
        </div>
      ) : sharedBy ? (
        <div className="pdv2-shared-by">Shared by {sharedBy}</div>
      ) : null}

      {/* Creator attribution + fork count */}
      <div className="pdv2-creator-row">
        {plan.creator_name && (
          <div className="pdv2-creator" onClick={(e) => setCreatorPopover({ name: plan.creator_name!, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() })}>
            <span className="pdv2-creator-avatar">
              {plan.creator_name.split(' ').map(w => w[0] || '').join('').slice(0, 2).toUpperCase()}
            </span>
            <span className="pdv2-creator-name">{plan.creator_name}</span>
          </div>
        )}
        {plan.used_count > 0 && (
          <span className="pdv2-forks">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 3v6M18 3v6M6 9c0 3 6 3 6 6v3M18 9c0 3-6 3-6 6"/><circle cx="6" cy="3" r="2"/><circle cx="18" cy="3" r="2"/><circle cx="12" cy="21" r="2"/></svg>
            {fmt(plan.used_count)}
          </span>
        )}
      </div>

      {/* Title + meta */}
      <div className="pdv2-title-section">
        <h1 className="pdv2-inline-title">{plan.title}</h1>
        <p className="pdv2-inline-meta">{plan.duration_days} days · {totalActivities} activities</p>
      </div>

      {/* Group Pulse */}
      {voteSummary && voteSummary.total_voters > 0 && (
        <div className="pdv2-group-pulse">
          <div className="pdv2-pulse-header">
            <span className="pdv2-pulse-icon">📊</span>
            <span className="pdv2-pulse-title">Group Pulse</span>
            <span className="pdv2-pulse-voters">{voteSummary.total_voters} people voted</span>
          </div>
          <div className="pdv2-pulse-stats">
            {voteSummary.top_pick && (
              <div className="pdv2-pulse-stat">
                <span className="pdv2-pulse-label">Top pick</span>
                <span className="pdv2-pulse-value">{voteSummary.top_pick}</span>
              </div>
            )}
            {voteSummary.most_debated && (
              <div className="pdv2-pulse-stat">
                <span className="pdv2-pulse-label">Most debated</span>
                <span className="pdv2-pulse-value pdv2-pulse-value--debated">{voteSummary.most_debated}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA row — Save + Suggest */}
      <div className="pdv2-cta-row">
        <button className="pdv2-fork-btn" onClick={handleFork}>
          {inviteContext && !user ? 'Save your votes — create a free account' : user ? 'Make it mine' : 'Save to my plans'}
        </button>
        <button className="pdv2-suggest-btn" onClick={() => setShowSuggest(true)}>
          💡 Suggest
        </button>
      </div>

      {/* Reporting this plan. Quiet by design — it needs to be findable by
          someone who wants it, not to compete with the actions everyone else
          came for. Play requires it to exist for content another user wrote. */}
      <div className="pdv2-report-row">
        <button
          type="button"
          className="pdv2-report-btn"
          onClick={() => setShowReport(true)}
        >
          Report this plan
        </button>
      </div>

      <ReportContentModal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        targetType="community_plan"
        targetId={String(plan.id)}
        targetLabel={plan.title}
        context="plan detail"
      />

      {/* Highlights */}
      {plan.highlights.length > 0 && (
        <div className="pdv2-highlights">
          {plan.highlights.map((h, i) => (
            <span key={i} className="pdv2-highlight">{h}</span>
          ))}
        </div>
      )}

      {/* Map */}
      {mapMarkers.length > 0 && (
        <div className="pdv2-map">
          <UnifiedMap markers={mapMarkers} />
        </div>
      )}

      {/* Intel bullets */}
      {plan.intel && Array.isArray((plan.intel as Record<string, unknown>).intel_bullets) && (
        <div className="pdv2-intel">
          {((plan.intel as Record<string, unknown>).intel_bullets as Array<{ icon: string; text: string }>).map((b, i) => (
            <div key={i} className="pdv2-intel-bullet">
              <span className="pdv2-intel-icon">{b.icon}</span>
              <span className="pdv2-intel-text">{b.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Day-by-day */}
      <div className="pdv2-days-section">
        <div className="pdv2-days-header">
          <h2 className="pv2-section-label">Day by Day</h2>
          <button className="pdv2-collapse-btn" onClick={toggleAllDays}>
            {collapsedDays.size === 0 ? 'Collapse all' : 'Expand all'}
          </button>
        </div>

        {/* Day pills */}
        <div className="pdv2-day-pills">
          <button
            className={`pdv2-day-pill ${mapDay === null ? 'pdv2-day-pill--active' : ''}`}
            onClick={() => setMapDay(null)}
          >
            Overview
          </button>
          {plan.days.map(d => (
            <button
              key={d.day}
              className={`pdv2-day-pill ${mapDay === d.day ? 'pdv2-day-pill--active' : ''}`}
              onClick={() => setMapDay(d.day)}
            >
              Day {d.day}
            </button>
          ))}
        </div>

        {/* Day cards */}
        {(mapDay ? plan.days.filter(d => d.day === mapDay) : plan.days).map(day => {
          const isOpen = !collapsedDays.has(day.day);
          return (
            <div key={day.day} className="pdv2-day-card">
              <button className="pdv2-day-header" onClick={() => toggleDay(day.day)}>
                <div className="pdv2-day-info">
                  <span className="pdv2-day-num">Day {day.day}</span>
                  <span className="pdv2-day-title">{day.label}</span>
                  <span className="pdv2-day-summary">{day.activities.length} activities</span>
                </div>
                <ChevronRight
                  size={16}
                  className={`pdv2-day-chevron ${isOpen ? 'pdv2-day-chevron--open' : ''}`}
                />
              </button>

              {isOpen && (
                <div className="pdv2-day-body">
                  {day.activities.map((act, i) => (
                    <div key={i} className="pdv2-activity">
                      <div className="pdv2-activity-timeline">
                        <span className="pdv2-activity-num">{i + 1}</span>
                        {i < day.activities.length - 1 && <div className="pdv2-activity-connector" />}
                      </div>
                      <CommunityActivityThumb
              placeId={act.place_id}
              pool={act.pool}
              name={act.name}
            />
            <div className="pdv2-activity-content">
                        <span className="pdv2-activity-name">{act.name}</span>
                        {act.time && <span className="pdv2-activity-time">{act.time}</span>}
                        <div className="pdv2-vote-row">
                          <button
                            className={`pdv2-vote-btn ${votes[act.name] === 'interested' ? 'pdv2-vote-btn--active-yes' : ''}`}
                            onClick={() => handleVote(act.name, day.day, 'interested')}
                          >
                            👍 Interested{(voteSummary?.activities[act.name]?.interested || 0) > 0 ? ` · ${voteSummary?.activities[act.name]?.interested}` : ''}
                          </button>
                          <button
                            className={`pdv2-vote-btn ${votes[act.name] === 'not_for_me' ? 'pdv2-vote-btn--active-no' : ''}`}
                            onClick={() => handleVote(act.name, day.day, 'not_for_me')}
                          >
                            👎 Not for me
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    className="pdv2-view-day"
                    onClick={() => navigate(`/plans/p/${slug}/day/${day.day}`)}
                  >
                    View full day with map <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Crowd wisdom */}
      {plan.crowd_wisdom && plan.crowd_wisdom.sample > 0 && (
        <div className="pdv2-crowd">
          <div className="pdv2-crowd-header">{plan.crowd_wisdom.sample} people&rsquo;s edits</div>
          <div className="pdv2-crowd-cols">
            {plan.crowd_wisdom.removed.length > 0 && (
              <div className="pdv2-crowd-col">
                <span className="pdv2-crowd-label pdv2-crowd-label--removed">Removed</span>
                {plan.crowd_wisdom.removed.map((x, i) => (
                  <span key={i} className="pdv2-crowd-item">− {x}</span>
                ))}
              </div>
            )}
            {plan.crowd_wisdom.added.length > 0 && (
              <div className="pdv2-crowd-col">
                <span className="pdv2-crowd-label pdv2-crowd-label--added">Added</span>
                {plan.crowd_wisdom.added.map((x, i) => (
                  <span key={i} className="pdv2-crowd-item">+ {x}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* 3-step save modal (authed users) */}
      {showForkModal && plan && (
        <ForkModal
          planTitle={plan.title}
          destination={plan.destination}
          creatorName={plan.creator_name || 'VizDay'}
          durationDays={plan.duration_days}
          onConfirm={handleForkConfirm}
          onGoToPlan={(id) => navigate(`/itinerary/${id}?chat=1`)}
          onSharePlan={(id) => {
            setShowForkModal(false);
            setShowShareSheet(id);
          }}
          onClose={() => setShowForkModal(false)}
        />
      )}

      {/* Share sheet after save success */}
      {showShareSheet && (
        <ShareSheet
          itineraryId={showShareSheet}
          title={plan?.title || ''}
          destination={plan?.destination || ''}
          currentVisibility="private"
          onClose={() => {
            const id = showShareSheet;
            setShowShareSheet(null);
            navigate(`/itinerary/${id}`);
          }}
        />
      )}

      {showSuggest && plan && (
        <SuggestModal
          planId={plan.id}
          creatorName={plan.creator_name || 'VizDay'}
          isAuthed={!!user}
          onClose={() => setShowSuggest(false)}
          onSignUp={() => {
            setShowSuggest(false);
            if (slug) storeForkIntent(slug);
            navigate('/login', { state: { returnTo: window.location.pathname } });
          }}
        />
      )}

      {creatorPopover && (
        <CreatorMiniPanel
          creatorName={creatorPopover.name}
          anchorRect={creatorPopover.rect}
          onClose={() => setCreatorPopover(null)}
        />
      )}

      <BottomNav activeItem="explore" />
    </div>
  );
};

export default PlanDetailV2;
